import { inject, Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import {
  ComparisonQueryFormGroupValue,
  ComplexFilterService,
} from '@blaze-runtime/engine-ui-feature';
import {
  IntelliConfig,
  IntelliSenseService,
  QueryOperatorOption,
} from '@blaze-shared/intellisense-feature';
import {
  ColumnFieldOption,
  DatasetSchema,
  findColumn,
  mapColumnsToOptions,
} from '@blaze-shared/schema-domain';
import { SelectOption } from '@blaze-shared/shared-util';

@Injectable()
export class RollupComplexFilterService implements ComplexFilterService {
  private readonly iss = inject(IntelliSenseService);

  private readonly rollupContext$ = new BehaviorSubject<DatasetSchema | null>(null);

  readonly showIgnoreWhenEmptyToggle = signal(true);

  setup(rollupContext: DatasetSchema): void {
    this.rollupContext$.next(rollupContext);
  }

  getColumns(): Observable<SelectOption<ColumnFieldOption>[]> {
    return this.rollupContext$.pipe(
      filter(Boolean),
      switchMap((rollupContext) => {
        return this.iss.getOptionsForColumnsInDataset(rollupContext.id);
      }),
      map((columns) => mapColumnsToOptions(columns)),
    );
  }

  getOperators(
    queryCondition$: Observable<ComparisonQueryFormGroupValue>,
  ): Observable<QueryOperatorOption[]> {
    return combineLatest({
      queryCondition: queryCondition$.pipe(startWith(null)),
      rollupContext: this.rollupContext$.pipe(filter(Boolean)),
    }).pipe(
      concatMap(({ queryCondition, rollupContext }) => {
        const column = queryCondition
          ? findColumn(rollupContext?.columns, queryCondition.column)
          : null;
        return this.iss.getOptionsForQueryOperators(column);
      }),
    );
  }

  getIntelliConfig(
    queryCondition$: Observable<ComparisonQueryFormGroupValue>,
  ): Observable<IntelliConfig> {
    return combineLatest({
      queryCondition: queryCondition$,
      rollupContext: this.rollupContext$.pipe(filter(Boolean)),
    }).pipe(
      map(({ queryCondition, rollupContext }) => {
        return {
          column: findColumn(rollupContext.columns, queryCondition.column) || null,
          datasetSchema: rollupContext,
          inputSchema: rollupContext,
          isBlazeTableFormulaColumn: true,
          pageContextSchema: null,
          schemaColumns: rollupContext.columns,
          fixedValuesSchema: rollupContext,

          id: queryCondition.formControlId ?? queryCondition.column,
          operator: queryCondition.operator,
          valueInfo: {
            displayInfos: queryCondition.displayInfos ?? [],
            isFormulaValue: queryCondition.isFormulaValue,
            value: queryCondition.value ?? '',
          },
        } as IntelliConfig;
      }),
    );
  }

  getErrors(): Observable<string[]> {
    return of([]);
  }
}
